/*
 * Sergio Rossi: theme events for the dataLayer, pushed with window.srDataLayer
 * (snippets/sr-datalayer.liquid).
 */
(() => {
  if (!window.srDataLayer) return;

  const storage = {
    get(key) {
      try {
        return window.sessionStorage.getItem(key);
      } catch {
        return null;
      }
    },
    set(key, value) {
      try {
        window.sessionStorage.setItem(key, value);
      } catch {}
    },
    remove(key) {
      try {
        window.sessionStorage.removeItem(key);
      } catch {}
    },
  };

  /*
   * login: customer accounts are hosted by Shopify outside the theme, so a login is a customer
   * that was not logged on the previous page of the session.
   */
  const loginStatusKey = 'sr-datalayer:login-status';
  const loginStatus = window.srDataLayer.user.login_status;
  if (storage.get(loginStatusKey) === 'not_logged' && loginStatus === 'logged') {
    window.srDataLayer.push('login');
  }
  storage.set(loginStatusKey, loginStatus);

  /*
   * newsletter_subscription and contact_form: the forms post and reload the page, with
   * customer_posted or contact_posted in the URL when the submission succeeds. The submitted
   * form is remembered, so a reload of that page and the back in stock form (a contact form
   * too) are not counted. The bot challenge page sits between submit and result.
   */
  const formKey = 'sr-datalayer:form';
  const formEvents = { customer: 'newsletter_subscription', contact: 'contact_form' };

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      const eventName = formEvents[form.elements?.form_type?.value];
      if (eventName && !form.matches('[data-notification-form]')) storage.set(formKey, eventName);
    },
    true
  );

  if (!window.location.pathname.endsWith('/challenge')) {
    const submitted = storage.get(formKey);
    const params = new URLSearchParams(window.location.search);
    storage.remove(formKey);

    if (
      (submitted === 'newsletter_subscription' && params.get('customer_posted') === 'true') ||
      (submitted === 'contact_form' && params.get('contact_posted') === 'true')
    ) {
      window.srDataLayer.push(submitted);
    }
  }

  /*
   * view_item_list and select_item. Product grid items carry their item data
   * (snippets/sr-datalayer-item.liquid) and belong to the list of the grid they sit in: the
   * collection or the search results, related products, recently viewed. Other grids are not
   * tracked. Each list sends all its products in one event, as the SDD asks: the page lists on
   * load, then the products added later (lazy loaded lists, infinite scroll, filters) in a new
   * event per list.
   */
  const lists = [
    ['recently-viewed', { item_list_id: 'recently_viewed', item_list_name: 'recently viewed' }],
    ['.tabs__recommendation', { item_list_id: 'related_products', item_list_name: 'related products' }],
    ['collection-component', window.srDataLayer.list],
  ];

  const listItem = (gridItem) => {
    const data = gridItem.querySelector('[data-sr-item]');
    if (!data) return null;

    for (const [selector, list] of lists) {
      const container = list && gridItem.closest(selector);
      if (!container) continue;

      const index = [...container.querySelectorAll('[data-sr-item]')].indexOf(data);
      return { list, item: { ...JSON.parse(data.textContent), ...list, index } };
    }
    return null;
  };

  const listedItems = new Map();
  const reportedItems = new WeakSet();
  let listedTimer;

  const pushListedItems = () => {
    clearTimeout(listedTimer);
    listedItems.forEach((items, list) => {
      window.srDataLayer.pushEcommerce('view_item_list', { ...list, items });
    });
    listedItems.clear();
  };

  const collectGridItems = (root) => {
    const gridItems = root.matches?.('[data-grid-item]') ? [root] : [...root.querySelectorAll('[data-grid-item]')];

    let added = false;
    gridItems.forEach((gridItem) => {
      const data = gridItem.querySelector('[data-sr-item]');
      if (!data || reportedItems.has(data)) return;

      const listed = listItem(gridItem);
      if (!listed) return;
      reportedItems.add(data);
      if (!listedItems.has(listed.list)) listedItems.set(listed.list, []);
      listedItems.get(listed.list).push(listed.item);
      added = true;
    });

    // Waits for the rest of a batch of cards rendered together
    if (!added) return;
    clearTimeout(listedTimer);
    listedTimer = setTimeout(pushListedItems, 300);
  };

  collectGridItems(document);
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) collectGridItems(node);
      });
    });
  }).observe(document.body, { childList: true, subtree: true });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') pushListedItems();
  });

  // Motif points the card links to a sibling color on hover: that is another product, not tracked
  // Search results add their own query string to the card URL (_pos, _sid, _ss)
  const productHandle = (path) => path.split(/[?#]/)[0].split('/products/')[1]?.split('/')[0];

  document.addEventListener(
    'click',
    (event) => {
      const link = event.target.closest?.('a[href]');
      const gridItem = link?.closest('[data-grid-item]');
      if (!gridItem || productHandle(link.pathname) !== productHandle(gridItem.dataset.url || '')) return;

      const listed = listItem(gridItem);
      if (listed) window.srDataLayer.pushEcommerce('select_item', { ...listed.list, items: [listed.item] });
    },
    true
  );

  /*
   * add_to_cart, remove_from_cart and view_cart. The cart lines are kept as they were at the last
   * check and compared with Motif's add response and with /cart.js after every cart render
   * (theme:cart:change), so a quantity change sends only the difference. The add response is
   * applied right away: with the cart page setting Motif leaves the page after adding.
   */
  const cartItem = (line, quantity = line.quantity) => ({
    item_id: String(line.product_id),
    item_name: line.product_title,
    item_brand: line.vendor,
    item_category: line.product_type,
    item_variant: line.variant_options.join(' / '),
    item_variant_id: String(line.variant_id),
    item_variant_sku: line.sku,
    price: line.final_price / 100,
    discount: (line.original_price - line.final_price) / 100,
    quantity,
  });

  const pushCartItems = (event, items) => {
    if (!items.length) return;
    const value = items.reduce((total, item) => total + item.price * item.quantity, 0);
    window.srDataLayer.pushEcommerce(event, { value: Math.round(value * 100) / 100, items });
  };

  const cartLines = new Map(window.srDataLayer.cart.items.map((line) => [line.key, line]));

  // lines: the whole cart, or with complete false only the lines of an add response
  const updateCart = (lines, complete = true) => {
    const added = [];
    const removed = [];

    lines.forEach((line) => {
      const quantity = line.quantity - (cartLines.get(line.key)?.quantity || 0);
      if (quantity > 0) added.push(cartItem(line, quantity));
      if (quantity < 0) removed.push(cartItem(line, -quantity));
    });

    if (complete) {
      const keys = new Set(lines.map((line) => line.key));
      cartLines.forEach((line, key) => {
        if (!keys.has(key)) removed.push(cartItem(line));
      });
      cartLines.clear();
    }
    lines.forEach((line) => cartLines.set(line.key, line));

    pushCartItems('add_to_cart', added);
    pushCartItems('remove_from_cart', removed);
  };

  let cartSync = Promise.resolve();
  let cartChangeTimer;
  const syncCart = () => {
    cartSync = cartSync
      .then(() => fetch(`${window.theme?.routes?.cart_url || '/cart'}.js`))
      .then((response) => response.json())
      .then((cart) => updateCart(cart.items))
      .catch(() => {});
  };

  // Motif sends this event also for an error response (e.g. not enough stock): no line key there
  document.addEventListener('theme:product:add', (event) => {
    const response = event.detail?.response;
    const lines = (response?.items || [response]).filter((line) => line?.key);
    if (lines.length) updateCart(lines, false);
  });

  document.addEventListener('theme:cart:change', () => {
    clearTimeout(cartChangeTimer);
    cartChangeTimer = setTimeout(syncCart, 100);
  });

  const pushViewCart = () => pushCartItems('view_cart', [...cartLines.values()].map((line) => cartItem(line)));

  document.addEventListener('theme:cart-drawer:open', () => cartSync.then(pushViewCart));
  if (document.body.classList.contains('template-cart')) pushViewCart();

  /*
   * viewpage for pages loaded without a reload: Motif swaps products (product-info.js) and
   * filter links (collection-filters-form.js) through the history API. Only a new path is a new
   * page, filters and variants change the query string alone.
   */
  let path = window.location.pathname;
  const onLocationChange = () => {
    if (window.location.pathname === path) return;
    path = window.location.pathname;
    window.srDataLayer.push('viewpage');
  };

  ['pushState', 'replaceState'].forEach((method) => {
    const original = window.history[method];
    window.history[method] = function (...args) {
      const result = original.apply(this, args);
      onLocationChange();
      return result;
    };
  });
  window.addEventListener('popstate', onLocationChange);
})();
